// Central icon registry: resolves to custom uploaded icons first, then FA5
// Usage: getImageIcon(image), getRegionIcon(slug), getActionIcon(name)

import {
  FaUbuntu,
  FaLinux,
  FaDocker,
  FaWordpress,
  FaJoomla,
  FaMagento,
  FaShieldAlt,
  FaLightbulb,
  FaCubes,
  FaServer,
  FaThLarge,
  FaGlobeAmericas,
  FaGlobeEurope,
  FaGlobeAsia,
  FaMapMarkerAlt,
  FaPlay,
  FaPowerOff,
  FaStop,
  FaRedo,
  FaTerminal,
  FaTrash,
  FaKey,
  FaSyncAlt,
} from "react-icons/fa";
import { url } from "./pubUrl";

let overridesLoaded = false;
let overrides = {};

async function loadOverridesOnce() {
  if (overridesLoaded) return overrides;
  try {
    const res = await fetch(url("icons/icons.custom.json"), {
      cache: "no-store",
    });
    if (res.ok) {
      overrides = await res.json();
    }
  } catch (_) {}
  overridesLoaded = true;
  return overrides;
}

function normalizeSlug(s) {
  const str = String(s || "").toLowerCase();
  // try to reduce ubuntu-24-04-x64 → ubuntu
  const base = str.split("-")[0];
  return { base, full: str };
}

function buildCustomCandidates(type, slug) {
  // 1) explicit override mapping
  const key = `${type}:${slug.full}`;
  const keyBase = `${type}:${slug.base}`;
  const cand = [];
  const o = overrides || {};
  if (o[key]) cand.push(o[key]);
  if (o[keyBase]) cand.push(o[keyBase]);
  cand.push(url(`icons/${type}/${slug.full}.png`));
  return cand;
}

// Built-in FA mappings
const osMap = {
  ubuntu: { Icon: FaUbuntu, color: "#E95420" },
  debian: { Icon: FaLinux, color: "#A81D33" },
  centos: { Icon: FaLinux },
  almalinux: { Icon: FaLinux },
  rocky: { Icon: FaLinux },
  fedora: { Icon: FaLinux },
  freebsd: { Icon: FaLinux },
  arch: { Icon: FaLinux },
};

const appMap = {
  wordpress: { Icon: FaWordpress, color: "#21759B" },
  docker: { Icon: FaDocker, color: "#2496ED" },
  dokku: { Icon: FaDocker },
  lamp: { Icon: FaLightbulb },
  lemp: { Icon: FaCubes },
  cyberpanel: { Icon: FaShieldAlt },
  joomla: { Icon: FaJoomla, color: "#1e88e5" },
  magento: { Icon: FaMagento, color: "#f26322" },
};

const actionMap = {
  play: FaPlay,
  poweroff: FaPowerOff,
  stop: FaStop,
  redo: FaRedo,
  terminal: FaTerminal,
  trash: FaTrash,
  key: FaKey,
  sync: FaSyncAlt,
};

// Map region codes to country codes for flag icons
const regionToCountryMap = {
  nyc: "us", // New York, USA
  sfo: "us", // San Francisco, USA
  tor: "ca", // Toronto, Canada
  lon: "gb", // London, UK
  ams: "nl", // Amsterdam, Netherlands
  fra: "de", // Frankfurt, Germany
  sgp: "sg", // Singapore
  blr: "in", // Bangalore, India
  syd: "au", // Sydney, Australia
  atl: "us", // Atlanta, USA
};

function regionIconFor(slug) {
  const s = String(slug || "").toLowerCase();
  if (s.startsWith("nyc") || s.startsWith("sfo") || s.startsWith("tor"))
    return { Icon: FaGlobeAmericas, label: slug?.toUpperCase() };
  if (s.startsWith("lon") || s.startsWith("ams") || s.startsWith("fra"))
    return { Icon: FaGlobeEurope, label: slug?.toUpperCase() };
  if (
    s.startsWith("sgp") ||
    s.startsWith("blr") ||
    s.startsWith("syd") ||
    s.startsWith("atl")
  )
    return { Icon: FaGlobeAsia, label: slug?.toUpperCase() };
  return { Icon: FaMapMarkerAlt, label: slug?.toUpperCase() };
}

export async function getImageIcon(image) {
  await loadOverridesOnce();
  const raw = image?.slug || image?.name || "";
  const slug = normalizeSlug(raw);
  // try app map first, then os map
  const cand = buildCustomCandidates("os", slug);
  if (cand.length) {
    return { type: "img", src: cand[0], label: raw };
  }
  const m = appMap[slug.base] || osMap[slug.base];
  if (m) return { type: "icon", ...m, label: raw };
  return { type: "icon", Icon: FaServer, label: raw };
}

export async function getRegionIcon(regionSlug) {
  await loadOverridesOnce();
  // Try to map region to country flag
  const regionPrefix = String(regionSlug || "")
    .toLowerCase()
    .substring(0, 3);
  const countryCode = regionToCountryMap[regionPrefix];
  if (countryCode) {
    return {
      type: "img",
      src: url(`icons/flags/${countryCode}.png`),
      label: regionSlug?.toUpperCase(),
    };
  }

  // Fallback to icon
  const m = regionIconFor(regionSlug);
  return { type: "icon", ...m };
}

export function getActionIcon(name) {
  const Icon = actionMap[name] || FaThLarge;
  return { type: "icon", Icon, label: name };
}
